import type { VenueCategory } from '../types';
import {
  Utensils, Coffee, Landmark, Eye, Building2,
  TreePine, ShoppingBag, Sparkles, Train, Waves, Mountain, SquareParking, MapPin,
  Footprints, Car, Bike,
  type LucideIcon,
} from '../components/ui/Icon';
import type { Libraries } from '@react-google-maps/api';

export const MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
export const LIBRARIES: Libraries = ['places', 'geometry'];
export const MAP_DEFAULT_CENTER = { lat: 38.7169, lng: -9.1395 };
export const MAP_DEFAULT_ZOOM   = 12;

export const LEG_MODES: { mode: string; Icon: LucideIcon }[] = [
  { mode: 'WALKING',   Icon: Footprints },
  { mode: 'DRIVING',   Icon: Car        },
  { mode: 'BICYCLING', Icon: Bike       },
  { mode: 'TRANSIT',   Icon: Train      },
];

export const CAT_META: Record<VenueCategory, { Icon: LucideIcon; color: string; markerColor: string }> = {
  RESTAURANT:    { Icon: Utensils,    color: 'text-orange-500', markerColor: '#ea580c' },
  CAFE:          { Icon: Coffee,      color: 'text-amber-600',  markerColor: '#d97706' },
  ATTRACTION:    { Icon: Landmark,    color: 'text-violet-500', markerColor: '#7c3aed' },
  VIEWPOINT:     { Icon: Eye,         color: 'text-sky-500',    markerColor: '#0284c7' },
  NEIGHBORHOOD:  { Icon: Building2,   color: 'text-indigo-500', markerColor: '#4f46e5' },
  PARK:          { Icon: TreePine,    color: 'text-green-500',  markerColor: '#16a34a' },
  SHOP:          { Icon: ShoppingBag, color: 'text-pink-500',   markerColor: '#db2777' },
  EXPERIENCE:    { Icon: Sparkles,    color: 'text-teal-500',   markerColor: '#0d9488' },
  TRANSPORT_HUB: { Icon: Train,       color: 'text-slate-500',  markerColor: '#64748b' },
  BEACH:         { Icon: Waves,         color: 'text-cyan-500',   markerColor: '#0891b2' },
  NATURE:        { Icon: Mountain,      color: 'text-lime-600',   markerColor: '#65a30d' },
  PARKING:       { Icon: SquareParking, color: 'text-blue-600',   markerColor: '#2563eb' },
  OTHER:         { Icon: MapPin,        color: 'text-stone-500',  markerColor: '#78716c' },
};

export function makePinIcon(color: string, visited = false) {
  const fill = visited ? '#94a3b8' : color;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="24" height="32">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 8 12 20 12 20S24 20 24 12C24 5.373 18.627 0 12 0z" fill="${fill}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="4.5" fill="white" opacity="0.9"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 24, height: 32 } as unknown as google.maps.Size,
    anchor:     { x: 12, y: 32 }          as unknown as google.maps.Point,
  };
}

export function makeSearchPinIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 48" width="36" height="48">
    <circle cx="18" cy="18" r="17" fill="#0d9488" opacity="0.2"/>
    <path d="M18 2C10.268 2 4 8.268 4 16c0 10.667 14 28 14 28S32 26.667 32 16C32 8.268 25.732 2 18 2z" fill="#0d9488" stroke="white" stroke-width="2"/>
    <circle cx="18" cy="16" r="6" fill="white" opacity="0.95"/>
    <circle cx="18" cy="16" r="3" fill="#0d9488"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 36, height: 48 } as unknown as google.maps.Size,
    anchor:     { x: 18, y: 48 }          as unknown as google.maps.Point,
  };
}

/** A stop pin carrying its position in the day, 1-based. */
export function makeNumberedPinIcon(n: number) {
  const fontSize = n >= 10 ? 9 : 11;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 36" width="28" height="36">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 22 14 22S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="#0d9488" stroke="white" stroke-width="1.5"/>
    <text x="14" y="${n >= 10 ? 17 : 18}" text-anchor="middle" fill="white" font-family="monospace" font-size="${fontSize}" font-weight="bold">${String(n)}</text>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 28, height: 36 } as unknown as google.maps.Size,
    anchor:     { x: 14, y: 36 }          as unknown as google.maps.Point,
  };
}
